import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { LanguageSwitch } from '../components/LanguageSwitch';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Checkbox } from '../components/ui/checkbox';
import { 
  Users, CheckCircle, Clock, LogOut, Search, 
  BarChart3, User, ChevronDown, ChevronUp, Briefcase,
  Trash2, RotateCcw, UserPlus, Shield, Edit, X,
  Filter, Calendar, ExternalLink, Zap
} from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// DISC colors
const DISC_COLORS = {
  D: '#ef4444',
  I: '#f59e0b',
  S: '#22c55e',
  C: '#3b82f6'
};

// Sphere labels
const SPHERE_LABELS = {
  SERVICES_FINANCIERS: { fr: "Services Financiers", en: "Financial Services" },
  SERVICES_AUX_ENTREPRISES: { fr: "Services aux Entreprises", en: "Business Services" },
  IMMOBILIER_ET_BATIMENT: { fr: "Immobilier et Bâtiment", en: "Real Estate & Construction" },
  RH_ET_FORMATION: { fr: "RH et Formation", en: "HR & Training" },
  SERVICES_AUX_PERSONNES: { fr: "Services aux Personnes", en: "Personal Services" },
  PUBLICITE_ET_COMMUNICATION: { fr: "Publicité et Communication", en: "Advertising & Communication" },
  SANTE_BIEN_ETRE: { fr: "Santé et Bien-être", en: "Health & Wellness" },
  TELECOM_ET_INFORMATIQUE: { fr: "Télécom et Informatique", en: "Telecom & IT" },
  FINANCE_AND_LEGAL: { fr: "Finance et Juridique", en: "Finance & Legal" },
  PROPERTY: { fr: "Immobilier", en: "Property" },
  BUSINESS_SUPPORT: { fr: "Support Business", en: "Business Support" },
  LIFESTYLE_SERVICES: { fr: "Services Lifestyle", en: "Lifestyle Services" },
};

export const AdminDashboard = () => {
  const { t, language } = useLanguage();
  const navigate = useNavigate();

  // Auth state
  const [role, setRole] = useState(null);
  const [adminName, setAdminName] = useState('');
  
  // Tabs
  const [activeTab, setActiveTab] = useState('guests');
  
  // Guests state
  const [guests, setGuests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedGuest, setExpandedGuest] = useState(null);
  
  // Filters
  const [filterGroup, setFilterGroup] = useState('');
  const [filterDateFrom, setFilterDateFrom] = useState('');
  const [filterDateTo, setFilterDateTo] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [guestGroups, setGuestGroups] = useState([]);
  
  // Match popup (opens as centered browser popup window)
  const openMatchPopup = (url, title) => {
    const w = 1100, h = 750;
    const left = (window.screen.width - w) / 2;
    const top = (window.screen.height - h) / 2;
    window.open(url, title, `width=${w},height=${h},left=${left},top=${top},resizable=yes,scrollbars=yes,toolbar=no,menubar=no`);
  };
  
  // Future matches preview (read-only)
  const [futureMatches, setFutureMatches] = useState({}); // guest_name -> match data
  
  // Viewers state (super admin only)
  const [viewers, setViewers] = useState([]);
  const [availableGroups, setAvailableGroups] = useState([]);
  const [showViewerForm, setShowViewerForm] = useState(false);
  const [editingViewer, setEditingViewer] = useState(null);
  const [viewerForm, setViewerForm] = useState({ name: '', email: '', password: '', groups: [], allGroups: false });

  useEffect(() => {
    const token = localStorage.getItem('apollo_admin_token');
    const storedRole = localStorage.getItem('apollo_admin_role');
    const storedName = localStorage.getItem('apollo_admin_name');
    
    if (!token) {
      navigate('/admin-secure');
      return;
    }
    
    setRole(storedRole);
    setAdminName(storedName || '');
    
    fetchGuests(token);
    
    if (storedRole === 'super_admin') {
      fetchViewers(token);
      fetchAvailableGroups(token);
      fetchFutureMatches(token);
    }
  }, [navigate]);

  const fetchGuests = async (token) => {
    try {
      const response = await axios.get(`${API}/admin/guests`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setGuests(response.data);
      
      // Extract unique groups from guests
      const groups = [...new Set(response.data
        .map(g => g.scores?.group_name)
        .filter(Boolean)
      )].sort();
      setGuestGroups(groups);
    } catch (err) {
      console.error('Error fetching guests:', err);
      if (err.response?.status === 401) {
        handleLogout();
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchFutureMatches = async (tkn) => {
    try {
      const res = await fetch(`${API}/admin/matching/preview`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${tkn}` },
      });
      const data = await res.json();
      const map = {};
      for (const r of data.results || []) {
        if (r.status === 'MATCHED') {
          map[r.guest_name?.toUpperCase()] = r;
        }
      }
      setFutureMatches(map);
    } catch (err) {
      console.error('Error fetching future matches:', err);
    }
  };

  const fetchViewers = async (token) => {
    try {
      const response = await axios.get(`${API}/admin/viewers`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setViewers(response.data.viewers || []);
    } catch (err) {
      console.error('Error fetching viewers:', err);
    }
  };

  const fetchAvailableGroups = async (token) => {
    try {
      const response = await axios.get(`${API}/admin/available-groups`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAvailableGroups(response.data.groups || []);
    } catch (err) {
      console.error('Error fetching groups:', err);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('apollo_admin_token');
    localStorage.removeItem('apollo_admin_role');
    localStorage.removeItem('apollo_admin_groups');
    localStorage.removeItem('apollo_admin_name');
    navigate('/admin-secure');
  };

  const handleDelete = async (testId, guestName) => {
    if (role !== 'super_admin') return;
    if (!window.confirm(language === 'fr' 
      ? `Êtes-vous sûr de vouloir supprimer le test de ${guestName} ?` 
      : `Are you sure you want to delete ${guestName}'s test?`)) {
      return;
    }
    
    const token = localStorage.getItem('apollo_admin_token');
    try {
      await axios.delete(`${API}/admin/guest/${testId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setGuests(prev => prev.filter(g => g.test_id !== testId));
      setExpandedGuest(null);
    } catch (err) {
      console.error('Error deleting guest:', err);
      alert(language === 'fr' ? 'Erreur lors de la suppression' : 'Error deleting');
    }
  };

  const handleReset = async (testId, guestName) => {
    if (role !== 'super_admin') return;
    if (!window.confirm(language === 'fr' 
      ? `Êtes-vous sûr de vouloir réinitialiser le test de ${guestName} ?` 
      : `Are you sure you want to reset ${guestName}'s test?`)) {
      return;
    }
    
    const token = localStorage.getItem('apollo_admin_token');
    try {
      await axios.post(`${API}/admin/guest/${testId}/reset`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setGuests(prev => prev.map(g => 
        g.test_id === testId 
          ? { ...g, status: 'in_progress', completed_at: null, scores: null }
          : g
      ));
      setExpandedGuest(null);
    } catch (err) {
      console.error('Error resetting guest:', err);
      alert(language === 'fr' ? 'Erreur lors de la réinitialisation' : 'Error resetting');
    }
  };

  // Viewer management functions
  const handleSaveViewer = async () => {
    const token = localStorage.getItem('apollo_admin_token');
    const groups = viewerForm.allGroups ? ['all'] : viewerForm.groups;
    
    try {
      if (editingViewer) {
        await axios.put(`${API}/admin/viewers/${editingViewer}`, {
          name: viewerForm.name,
          password: viewerForm.password || null,
          groups: groups
        }, {
          headers: { Authorization: `Bearer ${token}` }
        });
      } else {
        await axios.post(`${API}/admin/viewers`, {
          email: viewerForm.email,
          name: viewerForm.name,
          password: viewerForm.password,
          groups: groups
        }, {
          headers: { Authorization: `Bearer ${token}` }
        });
      }
      
      fetchViewers(token);
      setShowViewerForm(false);
      setEditingViewer(null);
      setViewerForm({ name: '', email: '', password: '', groups: [], allGroups: false });
    } catch (err) {
      console.error('Error saving viewer:', err);
      alert(err.response?.data?.detail || 'Error saving viewer');
    }
  };

  const handleEditViewer = (viewer) => {
    const isAllGroups = viewer.groups && viewer.groups.length === 1 && viewer.groups[0] === 'all';
    setViewerForm({
      name: viewer.name,
      email: viewer.email,
      password: '',
      groups: isAllGroups ? [] : viewer.groups || [],
      allGroups: isAllGroups
    });
    setEditingViewer(viewer.email);
    setShowViewerForm(true);
  };

  const handleDeleteViewer = async (email) => {
    if (!window.confirm(t('admin.viewers.confirm_delete'))) return;
    
    const token = localStorage.getItem('apollo_admin_token');
    try {
      await axios.delete(`${API}/admin/viewers/${email}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchViewers(token);
    } catch (err) {
      console.error('Error deleting viewer:', err);
    }
  };

  const toggleGroup = (group) => {
    setViewerForm(prev => ({
      ...prev,
      groups: prev.groups.includes(group)
        ? prev.groups.filter(g => g !== group)
        : [...prev.groups, group]
    }));
  };

  // Filter guests by search, group, and date
  const filteredGuests = guests.filter(guest => {
    // Search filter
    const matchesSearch = !searchQuery || 
      guest.guest_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      guest.guest_id?.toLowerCase().includes(searchQuery.toLowerCase());
    
    // Group filter
    const matchesGroup = !filterGroup || guest.scores?.group_name === filterGroup;
    
    // Date filter (invitation_date)
    let matchesDate = true;
    if (filterDateFrom || filterDateTo) {
      const invitationDate = guest.scores?.invitation_date;
      if (invitationDate) {
        const invDate = new Date(invitationDate).getTime();
        if (filterDateFrom) {
          const fromDate = new Date(filterDateFrom).getTime();
          matchesDate = matchesDate && invDate >= fromDate;
        }
        if (filterDateTo) {
          const toDate = new Date(filterDateTo).setHours(23, 59, 59, 999);
          matchesDate = matchesDate && invDate <= toDate;
        }
      } else {
        matchesDate = false; // No invitation date, exclude if filtering by date
      }
    }
    
    return matchesSearch && matchesGroup && matchesDate;
  });

  const completedCount = filteredGuests.filter(g => g.status === 'completed').length;
  const pendingCount = filteredGuests.filter(g => g.status === 'in_progress').length;
  const matchedCount = filteredGuests.filter(g => g.scores?.link1).length;

  const clearFilters = () => {
    setFilterGroup('');
    setFilterDateFrom('');
    setFilterDateTo('');
  };

  // Matching functions
  const toggleExpand = (guestId) => {
    setExpandedGuest(prev => prev === guestId ? null : guestId);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleDateString(language === 'fr' ? 'fr-FR' : 'en-US', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getDominantDISC = (scores) => {
    if (!scores) return [];
    const discData = [
      ["D", scores.disc_d || 0],
      ["I", scores.disc_i || 0],
      ["S", scores.disc_s || 0],
      ["C", scores.disc_c || 0]
    ].sort((a, b) => b[1] - a[1]);
    
    if (discData.length === 0) return [];
    const dominant = [discData[0]];
    if (discData.length > 1 && discData[0][1] - discData[1][1] <= 5) {
      dominant.push(discData[1]);
    }
    return dominant;
  };

  const getTop3Values = (scores) => {
    if (!scores) return [];
    const values = [];
    if (scores.valeur_1) values.push(scores.valeur_1);
    if (scores.valeur_2) values.push(scores.valeur_2);
    if (scores.valeur_3) values.push(scores.valeur_3);
    return values;
  };

  const isSuperAdmin = role === 'super_admin';

  const formatInvitationDate = (dateStr) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleDateString(language === 'fr' ? 'fr-FR' : 'en-US', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-black">
      {/* Header */}
      <header className="sticky top-0 z-50 px-6 py-4 flex justify-between items-center bg-gray-900/80 backdrop-blur-sm border-b border-gray-800">
        <div className="flex items-center gap-3">
          <img src="/apollo-logo.png" alt="Apollo" className="h-8 w-auto" />
          <span className="text-gray-400 font-mono text-sm">/ guest-admin</span>
          {!isSuperAdmin && (
            <Badge className="bg-orange-500/20 text-orange-500 border-orange-500/30 ml-2">
              Viewer
            </Badge>
          )}
        </div>
        <div className="flex items-center gap-4">
          {/* BPM Switch Button */}
          <a
            href="https://bpm.apollo-business.com/admin-secure"
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-2 px-3 py-2 rounded-md bg-gray-800 hover:bg-gray-700 text-orange-500 text-sm font-medium transition-colors"
          >
            <ExternalLink className="w-4 h-4" />
            BPM
          </a>
          <span className="text-gray-400 text-sm hidden sm:block">{adminName}</span>
          <LanguageSwitch />
          <Button
            data-testid="logout-btn"
            onClick={handleLogout}
            variant="ghost"
            className="text-gray-400 hover:text-white hover:bg-gray-800"
          >
            <LogOut className="w-4 h-4 mr-2" />
            {t('admin.dashboard.logout')}
          </Button>
        </div>
      </header>

      {/* Tabs (super admin only sees viewers tab) */}
      <div className="px-6 pt-6 max-w-7xl mx-auto">
        <div className="flex gap-2 mb-6">
          <Button
            onClick={() => setActiveTab('guests')}
            variant={activeTab === 'guests' ? 'default' : 'ghost'}
            className={activeTab === 'guests' ? 'bg-orange-500 hover:bg-orange-600' : 'text-gray-400 hover:text-white'}
          >
            <Users className="w-4 h-4 mr-2" />
            {t('admin.dashboard.guests')}
          </Button>
          {isSuperAdmin && (
            <Button
              onClick={() => setActiveTab('viewers')}
              variant={activeTab === 'viewers' ? 'default' : 'ghost'}
              className={activeTab === 'viewers' ? 'bg-orange-500 hover:bg-orange-600' : 'text-gray-400 hover:text-white'}
            >
              <Shield className="w-4 h-4 mr-2" />
              {t('admin.viewers.title')}
            </Button>
          )}
        </div>
      </div>

      {/* Main Content */}
      <main className="px-6 pb-8 max-w-7xl mx-auto">
        {activeTab === 'guests' && (
          <>
            {/* Stats Cards */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
              <Card className="bg-gray-800 border-gray-700">
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-gray-400 text-sm font-dm-sans">{t('admin.dashboard.total')}</p>
                      <p className="text-3xl font-manrope font-bold text-white">{guests.length}</p>
                    </div>
                    <div className="w-12 h-12 rounded-full bg-orange-500/20 flex items-center justify-center">
                      <Users className="w-6 h-6 text-orange-500" />
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-gray-800 border-gray-700">
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-gray-400 text-sm font-dm-sans">{t('admin.dashboard.completed')}</p>
                      <p className="text-3xl font-manrope font-bold text-green-500">{completedCount}</p>
                    </div>
                    <div className="w-12 h-12 rounded-full bg-green-500/20 flex items-center justify-center">
                      <CheckCircle className="w-6 h-6 text-green-500" />
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-gray-800 border-gray-700">
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-gray-400 text-sm font-dm-sans">{language === 'fr' ? 'Matchés' : 'Matched'}</p>
                      <p className="text-3xl font-manrope font-bold text-blue-400">{matchedCount}</p>
                    </div>
                    <div className="w-12 h-12 rounded-full bg-blue-500/20 flex items-center justify-center">
                      <Zap className="w-6 h-6 text-blue-400" />
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-gray-800 border-gray-700">
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-gray-400 text-sm font-dm-sans">{t('admin.dashboard.pending')}</p>
                      <p className="text-3xl font-manrope font-bold text-orange-500">{pendingCount}</p>
                    </div>
                    <div className="w-12 h-12 rounded-full bg-orange-500/20 flex items-center justify-center">
                      <Clock className="w-6 h-6 text-orange-500" />
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Search and Filters */}
            <div className="mb-6 space-y-4">
              <div className="flex flex-wrap gap-4 items-center">
                {/* Search */}
                <div className="relative flex-1 min-w-[200px] max-w-md">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
                  <Input
                    data-testid="search-input"
                    type="text"
                    placeholder={t('admin.dashboard.search')}
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-10 bg-gray-800 border-gray-700 focus:border-orange-500 text-white placeholder:text-gray-500"
                  />
                </div>
                
                {/* Filter Toggle */}
                <Button
                  onClick={() => setShowFilters(!showFilters)}
                  variant="ghost"
                  className={`text-gray-400 hover:text-white ${showFilters ? 'bg-gray-700' : ''}`}
                >
                  <Filter className="w-4 h-4 mr-2" />
                  {language === 'fr' ? 'Filtres' : 'Filters'}
                  {(filterGroup || filterDateFrom || filterDateTo) && (
                    <Badge className="ml-2 bg-orange-500 text-white">
                      {[filterGroup, filterDateFrom, filterDateTo].filter(Boolean).length}
                    </Badge>
                  )}
                </Button>
                
                {/* Clear Filters */}
                {(filterGroup || filterDateFrom || filterDateTo) && (
                  <Button
                    onClick={clearFilters}
                    variant="ghost"
                    size="sm"
                    className="text-red-400 hover:text-red-300"
                  >
                    <X className="w-4 h-4 mr-1" />
                    {language === 'fr' ? 'Effacer' : 'Clear'}
                  </Button>
                )}
              </div>
              
              {/* Filters Panel */}
              {showFilters && (
                <div className="p-4 bg-gray-900/50 rounded-lg border border-gray-700 grid grid-cols-1 md:grid-cols-3 gap-4">
                  {/* Group Filter */}
                  <div>
                    <Label className="text-gray-300 text-sm mb-2 block">
                      {language === 'fr' ? 'Groupe' : 'Group'}
                    </Label>
                    <select
                      value={filterGroup}
                      onChange={(e) => setFilterGroup(e.target.value)}
                      className="w-full bg-gray-800 border border-gray-700 rounded-md px-3 py-2 text-white focus:border-orange-500 focus:outline-none"
                    >
                      <option value="">{language === 'fr' ? 'Tous les groupes' : 'All groups'}</option>
                      {guestGroups.map(group => (
                        <option key={group} value={group}>{group}</option>
                      ))}
                    </select>
                  </div>
                  
                  {/* Date From Filter */}
                  <div>
                    <Label className="text-gray-300 text-sm mb-2 block flex items-center gap-2">
                      <Calendar className="w-4 h-4" />
                      {language === 'fr' ? 'Date invitation (du)' : 'Invitation date (from)'}
                    </Label>
                    <Input
                      type="date"
                      value={filterDateFrom}
                      onChange={(e) => setFilterDateFrom(e.target.value)}
                      className="bg-gray-800 border-gray-700 text-white"
                    />
                  </div>
                  
                  {/* Date To Filter */}
                  <div>
                    <Label className="text-gray-300 text-sm mb-2 block flex items-center gap-2">
                      <Calendar className="w-4 h-4" />
                      {language === 'fr' ? 'Date invitation (au)' : 'Invitation date (to)'}
                    </Label>
                    <Input
                      type="date"
                      value={filterDateTo}
                      onChange={(e) => setFilterDateTo(e.target.value)}
                      className="bg-gray-800 border-gray-700 text-white"
                    />
                  </div>
                </div>
              )}
            </div>

            {/* Guests List */}
            <Card className="bg-gray-800 border-gray-700">
              <CardHeader>
                <CardTitle className="text-white font-manrope flex items-center gap-2">
                  <Users className="w-5 h-5 text-orange-500" />
                  {t('admin.dashboard.guests')}
                </CardTitle>
              </CardHeader>
              <CardContent>
                {loading ? (
                  <div className="text-center py-12">
                    <div className="w-8 h-8 border-2 border-orange-500 border-t-transparent rounded-full animate-spin mx-auto"></div>
                  </div>
                ) : filteredGuests.length === 0 ? (
                  <div className="text-center py-12">
                    <User className="w-12 h-12 text-gray-600 mx-auto mb-3" />
                    <p className="text-gray-500 font-dm-sans">{t('admin.dashboard.no_guests')}</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {filteredGuests.map(guest => (
                      <div key={guest.id} className="border border-gray-700 rounded-lg overflow-hidden">
                        {/* Guest Row */}
                        <div 
                          className="flex items-center justify-between p-4 cursor-pointer hover:bg-gray-700/50 transition-colors"
                          onClick={() => guest.status === 'completed' && toggleExpand(guest.id)}
                        >
                          <div className="flex items-center gap-4">
                            <div className="w-10 h-10 rounded-full bg-gray-700 flex items-center justify-center">
                              <User className="w-5 h-5 text-gray-400" />
                            </div>
                            <div>
                              <div className="flex items-center gap-2">
                                <p className="text-white font-semibold font-manrope">{guest.guest_name || 'Invité'}</p>
                                {guest.scores?.group_name && (
                                  <span className="text-orange-500 text-sm font-dm-sans">• {guest.scores.group_name}</span>
                                )}
                              </div>
                              <div className="flex items-center gap-2 text-gray-500 text-xs">
                                <span className="font-dm-sans">
                                  {guest.scores?.invitant_name ? (
                                    <>{language === 'fr' ? 'Invité par' : 'Invited by'} <span className="text-gray-400">{guest.scores.invitant_name}</span></>
                                  ) : '-'}
                                </span>
                                {guest.scores?.invitation_date && (
                                  <>
                                    <span>•</span>
                                    <span className="flex items-center gap-1">
                                      <Calendar className="w-3 h-3" />
                                      {formatInvitationDate(guest.scores.invitation_date)}
                                    </span>
                                  </>
                                )}
                              </div>
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            {/* Match Buttons - existing matches */}
                            {guest.scores?.link1 && (
                              <button
                                onClick={(e) => { e.stopPropagation(); openMatchPopup(guest.scores.link1, `MATCH 1 — ${guest.guest_name}`); }}
                                className="px-2 py-1 rounded text-xs font-semibold bg-orange-500/20 text-orange-500 border border-orange-500/30 hover:bg-orange-500/30 transition-colors cursor-pointer"
                                data-testid="match1-btn"
                              >
                                MATCH 1
                              </button>
                            )}
                            {guest.scores?.link2 && (
                              <button
                                onClick={(e) => { e.stopPropagation(); openMatchPopup(guest.scores.link2, `MATCH 2 — ${guest.guest_name}`); }}
                                className="px-2 py-1 rounded text-xs font-semibold bg-blue-500/20 text-blue-400 border border-blue-500/30 hover:bg-blue-500/30 transition-colors cursor-pointer"
                                data-testid="match2-btn"
                              >
                                MATCH 2
                              </button>
                            )}
                            {/* Future match preview - only for unmatched completed guests */}
                            {!guest.scores?.link1 && guest.status === 'completed' && futureMatches[guest.guest_name?.toUpperCase()] && (
                              <span className="px-2 py-1 rounded text-xs bg-gray-700/50 text-gray-400 border border-dashed border-gray-600 flex items-center gap-1" data-testid="future-match-preview">
                                <Zap className="w-3 h-3 text-yellow-500" />
                                {futureMatches[guest.guest_name?.toUpperCase()].match1_name} — {futureMatches[guest.guest_name?.toUpperCase()].match1_score}%
                              </span>
                            )}
                            {/* Action Buttons - Super Admin Only */}
                            {isSuperAdmin && (
                              <>
                                <Button
                                  onClick={(e) => { e.stopPropagation(); handleReset(guest.test_id, guest.guest_name); }}
                                  variant="ghost"
                                  size="sm"
                                  className="text-orange-500 hover:text-orange-400 hover:bg-orange-500/10 p-2"
                                  title={language === 'fr' ? 'Réinitialiser' : 'Reset'}
                                >
                                  <RotateCcw className="w-4 h-4" />
                                </Button>
                                <Button
                                  onClick={(e) => { e.stopPropagation(); handleDelete(guest.test_id, guest.guest_name); }}
                                  variant="ghost"
                                  size="sm"
                                  className="text-red-500 hover:text-red-400 hover:bg-red-500/10 p-2"
                                  title={language === 'fr' ? 'Supprimer' : 'Delete'}
                                >
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              </>
                            )}
                            <Badge 
                              className={guest.status === 'completed' 
                                ? 'bg-green-500/20 text-green-500 border-green-500/30' 
                                : 'bg-orange-500/20 text-orange-500 border-orange-500/30'
                              }
                            >
                              {guest.status === 'completed' ? t('admin.dashboard.completed') : t('admin.dashboard.pending')}
                            </Badge>
                            <span className="text-gray-500 text-sm font-dm-sans hidden sm:block">
                              {formatDate(guest.created_at)}
                            </span>
                            {guest.status === 'completed' && (
                              expandedGuest === guest.id 
                                ? <ChevronUp className="w-5 h-5 text-gray-400" />
                                : <ChevronDown className="w-5 h-5 text-gray-400" />
                            )}
                          </div>
                        </div>

                        {/* Expanded Results */}
                        {expandedGuest === guest.id && guest.scores && (
                          <div className="border-t border-gray-700 p-6 bg-gray-900/50">
                            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                              {/* DISC Profile */}
                              <div>
                                <h4 className="text-white font-manrope font-semibold mb-4 flex items-center gap-2">
                                  <BarChart3 className="w-4 h-4 text-orange-500" />
                                  {t('admin.dashboard.disc_profile')}
                                </h4>
                                
                                {guest.scores && (
                                  <div className="mb-4 flex items-center gap-2">
                                    <span className="text-gray-400 text-sm">{language === 'fr' ? 'Dominance :' : 'Dominant:'}</span>
                                    <div className="flex gap-1">
                                      {getDominantDISC(guest.scores).map(([key, value]) => (
                                        <span 
                                          key={key}
                                          className="px-3 py-1 rounded-full text-white text-sm font-bold"
                                          style={{ backgroundColor: DISC_COLORS[key] }}
                                        >
                                          {key} ({value}%)
                                        </span>
                                      ))}
                                    </div>
                                  </div>
                                )}
                                
                                <div className="space-y-3">
                                  {["D", "I", "S", "C"].map((key) => {
                                    const discKey = `disc_${key.toLowerCase()}`;
                                    const value = guest.scores?.[discKey] || 0;
                                    const dominant = getDominantDISC(guest.scores || {});
                                    const isDominant = dominant.some(([k]) => k === key);
                                    return (
                                      <div key={key} className="flex items-center gap-3">
                                        <span 
                                          className={`w-8 h-8 rounded-full flex items-center justify-center text-white font-bold text-sm ${isDominant ? 'ring-2 ring-white ring-offset-2 ring-offset-gray-900' : ''}`}
                                          style={{ backgroundColor: DISC_COLORS[key] }}
                                        >
                                          {key}
                                        </span>
                                        <div className="flex-1 h-3 bg-gray-800 rounded-full overflow-hidden">
                                          <div 
                                            className="h-full rounded-full transition-all"
                                            style={{ width: `${value}%`, backgroundColor: DISC_COLORS[key] }}
                                          />
                                        </div>
                                        <span className={`text-sm w-12 text-right font-semibold ${isDominant ? 'text-white' : 'text-gray-400'}`}>{value}%</span>
                                      </div>
                                    );
                                  })}
                                </div>
                              </div>

                              {/* Values + Sphere + Country */}
                              <div>
                                <h4 className="text-white font-manrope font-semibold mb-4 flex items-center gap-2">
                                  <BarChart3 className="w-4 h-4 text-orange-500" />
                                  {t('admin.dashboard.values_profile')}
                                </h4>
                                
                                {guest.scores?.country && (
                                  <div className="mb-4 flex items-center gap-2">
                                    <span className="text-2xl">
                                      {guest.scores.country === 'UK' ? '🇬🇧' : '🇫🇷'}
                                    </span>
                                    <span className="text-gray-400 text-sm">
                                      {guest.scores.country === 'UK' ? 'United Kingdom' : 'France'}
                                    </span>
                                  </div>
                                )}
                                
                                <div className="flex flex-wrap gap-2 mb-4">
                                  {getTop3Values(guest.scores).map((label, index) => (
                                    <div 
                                      key={index} 
                                      className="bg-orange-500/20 border border-orange-500/30 rounded-full px-4 py-2"
                                    >
                                      <span className="text-orange-500 font-semibold text-sm">
                                        #{index + 1} {label}
                                      </span>
                                    </div>
                                  ))}
                                </div>
                                
                                {guest.scores?.user_sphere && (
                                  <div className="mt-4 pt-4 border-t border-gray-700">
                                    <div className="flex items-center gap-2">
                                      <Briefcase className="w-4 h-4 text-orange-500" />
                                      <span className="text-gray-400 text-sm">{language === 'fr' ? 'Sphère d\'activité :' : 'Activity sphere:'}</span>
                                    </div>
                                    <p className="text-white font-semibold mt-1">
                                      {SPHERE_LABELS[guest.scores.user_sphere]?.[language] || guest.scores.user_sphere}
                                    </p>
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </>
        )}

        {/* Viewers Tab (Super Admin Only) */}
        {activeTab === 'viewers' && isSuperAdmin && (
          <Card className="bg-gray-800 border-gray-700">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="text-white font-manrope flex items-center gap-2">
                <Shield className="w-5 h-5 text-orange-500" />
                {t('admin.viewers.title')}
              </CardTitle>
              <Button
                onClick={() => {
                  setViewerForm({ name: '', email: '', password: '', groups: [], allGroups: false });
                  setEditingViewer(null);
                  setShowViewerForm(true);
                }}
                className="bg-orange-500 hover:bg-orange-600"
              >
                <UserPlus className="w-4 h-4 mr-2" />
                {t('admin.viewers.add')}
              </Button>
            </CardHeader>
            <CardContent>
              {/* Viewer Form */}
              {showViewerForm && (
                <div className="mb-6 p-4 bg-gray-900/50 rounded-lg border border-gray-700">
                  <div className="flex justify-between items-center mb-4">
                    <h4 className="text-white font-semibold">
                      {editingViewer ? (language === 'fr' ? 'Modifier' : 'Edit') : t('admin.viewers.add')}
                    </h4>
                    <Button variant="ghost" size="sm" onClick={() => setShowViewerForm(false)}>
                      <X className="w-4 h-4" />
                    </Button>
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                    <div>
                      <Label className="text-gray-300">{t('admin.viewers.name')}</Label>
                      <Input
                        value={viewerForm.name}
                        onChange={(e) => setViewerForm(prev => ({ ...prev, name: e.target.value }))}
                        className="bg-gray-800 border-gray-700 text-white"
                      />
                    </div>
                    <div>
                      <Label className="text-gray-300">{t('admin.viewers.email')}</Label>
                      <Input
                        type="email"
                        value={viewerForm.email}
                        onChange={(e) => setViewerForm(prev => ({ ...prev, email: e.target.value }))}
                        disabled={!!editingViewer}
                        className="bg-gray-800 border-gray-700 text-white disabled:opacity-50"
                      />
                    </div>
                    <div>
                      <Label className="text-gray-300">
                        {t('admin.viewers.password')} {editingViewer && <span className="text-gray-500 text-xs">({language === 'fr' ? 'laisser vide pour ne pas changer' : 'leave empty to keep'})</span>}
                      </Label>
                      <Input
                        type="password"
                        value={viewerForm.password}
                        onChange={(e) => setViewerForm(prev => ({ ...prev, password: e.target.value }))}
                        className="bg-gray-800 border-gray-700 text-white"
                      />
                    </div>
                  </div>
                  
                  <div className="mb-4">
                    <Label className="text-gray-300 mb-2 block">{t('admin.viewers.groups')}</Label>
                    <div className="flex items-center gap-2 mb-3">
                      <Checkbox
                        id="all-groups"
                        checked={viewerForm.allGroups}
                        onCheckedChange={(checked) => setViewerForm(prev => ({ ...prev, allGroups: checked, groups: [] }))}
                      />
                      <label htmlFor="all-groups" className="text-white text-sm cursor-pointer">
                        {t('admin.viewers.all_groups')}
                      </label>
                    </div>
                    {!viewerForm.allGroups && (
                      <div className="flex flex-wrap gap-2 max-h-40 overflow-y-auto p-2 bg-gray-800 rounded border border-gray-700">
                        {availableGroups.map(group => (
                          <div
                            key={group}
                            onClick={() => toggleGroup(group)}
                            className={`px-3 py-1 rounded-full text-sm cursor-pointer transition-colors ${
                              viewerForm.groups.includes(group)
                                ? 'bg-orange-500 text-white'
                                : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                            }`}
                          >
                            {group}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                  
                  <div className="flex gap-2">
                    <Button onClick={handleSaveViewer} className="bg-orange-500 hover:bg-orange-600">
                      {t('admin.viewers.save')}
                    </Button>
                    <Button variant="ghost" onClick={() => setShowViewerForm(false)} className="text-gray-400">
                      {t('admin.viewers.cancel')}
                    </Button>
                  </div>
                </div>
              )}
              
              {/* Viewers List */}
              {viewers.length === 0 ? (
                <div className="text-center py-12">
                  <Shield className="w-12 h-12 text-gray-600 mx-auto mb-3" />
                  <p className="text-gray-500">{t('admin.viewers.no_viewers')}</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {viewers.map(viewer => (
                    <div key={viewer.email} className="flex items-center justify-between p-4 bg-gray-900/50 rounded-lg border border-gray-700">
                      <div>
                        <p className="text-white font-semibold">{viewer.name}</p>
                        <p className="text-gray-400 text-sm">{viewer.email}</p>
                        <div className="flex flex-wrap gap-1 mt-2">
                          {viewer.groups && viewer.groups[0] === 'all' ? (
                            <Badge className="bg-green-500/20 text-green-500 border-green-500/30">
                              {t('admin.viewers.all_groups')}
                            </Badge>
                          ) : (
                            viewer.groups?.map(group => (
                              <Badge key={group} className="bg-gray-700 text-gray-300">
                                {group}
                              </Badge>
                            ))
                          )}
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleEditViewer(viewer)}
                          className="text-orange-500 hover:text-orange-400"
                        >
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDeleteViewer(viewer.email)}
                          className="text-red-500 hover:text-red-400"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        )}
      </main>

    </div>

  );
};
